import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { MapPin } from "lucide-react";
import { IMAGES, STATS } from "@/lib/site-data";
import LeadForm from "@/components/site/LeadForm";

const lines = ["Global Education.", "Rooted in Odisha.", "Powered by AI."];

const container = {
  hidden: {},
  show: { transition: { staggerChildren: 0.12, delayChildren: 0.35 } },
};
const lineVariant = {
  hidden: { y: "110%" },
  show: { y: "0%", transition: { duration: 0.9, ease: [0.22, 1, 0.36, 1] } },
};

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const yImg = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const scaleImg = useTransform(scrollYProgress, [0, 1], [1.05, 1.2]);

  return (
    <section id="top" ref={ref} className="relative min-h-screen w-full grain overflow-hidden">
      {/* Parallax background */}
      <motion.div style={{ y: yImg, scale: scaleImg }} className="absolute inset-0 z-0">
        <img
          src={IMAGES.hero}
          alt="Graduates tossing their caps at graduation"
          className="h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-background via-background/75 to-background/10" />
        <div className="absolute inset-0 bg-gradient-to-t from-background via-transparent to-background/40" />
      </motion.div>

      <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 pt-32 pb-20 lg:pt-40 grid lg:grid-cols-12 gap-10 items-center min-h-screen">
        {/* Left: kinetic headline */}
        <div className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.7 }}
            className="inline-flex items-center gap-2 rounded-full border border-secondary/15 bg-secondary/[0.06] backdrop-blur-md px-4 py-1.5 text-xs uppercase tracking-[0.2em] text-secondary/90 mb-8"
            data-testid="hero-badge"
          >
            <MapPin size={13} className="text-primary" />
            Franchise Partner · MSM Unify & Career Banale
          </motion.div>

          <motion.h1
            variants={container}
            initial="hidden"
            animate="show"
            className="font-display font-medium leading-[0.95] tracking-tight text-secondary"
            style={{ fontSize: "clamp(2.75rem, 7.5vw, 5.5rem)" }}
            data-testid="hero-heading"
          >
            {lines.map((line, i) => (
              <span key={i} className="line-mask">
                <motion.span variants={lineVariant} className="block">
                  {i === 2 ? (
                    <>
                      Powered by <span className="italic text-primary">AI</span>.
                    </>
                  ) : (
                    line
                  )}
                </motion.span>
              </span>
            ))}
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.1, duration: 0.8 }}
            className="mt-8 max-w-xl text-base sm:text-lg text-foreground/75 leading-relaxed"
            data-testid="hero-sub"
          >
            Studyvia is Bhubaneswar's authorised franchise partner of MSM Unify
            and Career Banale — bringing all their study-abroad, Indian degree,
            test prep and career programs to Odisha under one roof.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.3, duration: 0.8 }}
            className="mt-10 flex flex-wrap items-center gap-8"
          >
            {STATS.map(([n, l]) => (
              <div key={l} data-testid="hero-stat">
                <div className="font-display text-3xl sm:text-4xl text-primary font-semibold">{n}</div>
                <div className="text-xs uppercase tracking-wider text-foreground/60 mt-1">{l}</div>
              </div>
            ))}
          </motion.div>
        </div>

        {/* Right: lead form */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="lg:col-span-5"
          id="contact"
        >
          <LeadForm />
        </motion.div>
      </div>
    </section>
  );
}
